/**
 * legalMarkdown — rendu des quatre documents juridiques.
 *
 * Pourquoi un rendu maison plutôt qu'une dépendance markdown : le corpus
 * est FERMÉ (quatre documents écrits et validés à la main, aucun contenu
 * utilisateur ne passe ici) et n'utilise que sept constructions. Ajouter
 * `react-markdown` + `remark-gfm` au bundle public pour ça, c'est payer
 * ~40 ko sur des pages que Google crawle, et ouvrir la porte au HTML brut
 * dans du texte de valeur juridique. Ce fichier ne rend QUE ce qu'il
 * connaît ; tout le reste ressort en texte, jamais en balise.
 *
 * Constructions supportées :
 *   # / ## / ###          titres (h1/h2/h3), ancrés pour la table des matières
 *   ---                   filet
 *   | a | b |             tableaux (1re ligne = en-tête, 2e = séparateur)
 *   - item                listes à puces
 *   **gras**  *italique*  emphase
 *   nom@archer.law        adresses → mailto:
 *   liens externes        UNIQUEMENT ceux de EXTERNAL_LINKS ci-dessous
 *
 * Les sauts de ligne simples DANS un paragraphe deviennent des <br> :
 *   les blocs d'adresse (siège social, hébergeur) tiennent leur sens de
 *   leur mise en ligne.
 */
import React from 'react';

/**
 * Liens externes autorisés — liste EXPLICITE, pas une détection de
 * domaines. Un « auto-lien » générique transformerait « art. 6.1.b » ou
 * « VI.53 » en URL et fabriquerait des liens morts dans un document
 * juridique. Ici, chaque entrée est une URL vérifiée à la main : le texte
 * exact tel qu'il apparaît dans le document → sa destination.
 */
const EXTERNAL_LINKS = {
  'mediationconsommateur.be': 'https://mediationconsommateur.be',
  'ec.europa.eu/consumers/odr': 'https://ec.europa.eu/consumers/odr',
  'www.autoriteprotectiondonnees.be': 'https://www.autoriteprotectiondonnees.be',
  'railway.com': 'https://railway.com',
};

// Trié du plus long au plus court : « ec.europa.eu/consumers/odr » doit
// gagner avant qu'un préfixe plus court ne morde dessus.
const EXTERNAL_KEYS = Object.keys(EXTERNAL_LINKS).sort((a, b) => b.length - a.length);

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const EXTERNAL_RE = new RegExp(`(${EXTERNAL_KEYS.map(escapeRe).join('|')})`, 'g');
const EMAIL_RE = /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
const BOLD_RE = /\*\*([^*]+)\*\*/g;
// Appliquée APRÈS le gras : à ce stade il ne reste plus une seule paire
// de `**` dans les chaînes, donc pas besoin de lookbehind (que Babel ne
// transpile pas et que tous les navigateurs cibles ne garantissent pas).
const ITALIC_RE = /\*([^*\n]+)\*/g;

/** Identifiant d'ancre stable pour un titre (table des matières + #hash). */
export function slugify(text) {
  return `sec-${String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')}`;
}

/**
 * Applique un découpage à la liste de nœuds : les chaînes sont coupées
 * sur `regex`, les éléments React déjà produits sont laissés tels quels
 * (c'est ce qui évite de re-traiter l'intérieur d'un <strong>).
 */
function splitNodes(nodes, regex, render) {
  const out = [];
  nodes.forEach((node, ni) => {
    if (typeof node !== 'string') { out.push(node); return; }
    const parts = node.split(regex);
    parts.forEach((part, pi) => {
      if (part === '' || part == null) return;
      // Avec une regex à un seul groupe capturant, les captures sont aux
      // index impairs.
      if (pi % 2 === 1) out.push(render(part, `${ni}-${pi}`));
      else out.push(part);
    });
  });
  return out;
}

/** Rendu du contenu inline d'une ligne (gras, italique, mailto, liens). */
export function renderInline(text, keyPrefix = 'i') {
  let nodes = [text];
  nodes = splitNodes(nodes, BOLD_RE, (v, k) => <strong key={`${keyPrefix}-b${k}`}>{renderInline(v, `${keyPrefix}-b${k}`)}</strong>);
  nodes = splitNodes(nodes, ITALIC_RE, (v, k) => <em key={`${keyPrefix}-e${k}`}>{v}</em>);
  nodes = splitNodes(nodes, EMAIL_RE, (v, k) => (
    <a key={`${keyPrefix}-m${k}`} href={`mailto:${v}`} className="legal-link">{v}</a>
  ));
  nodes = splitNodes(nodes, EXTERNAL_RE, (v, k) => (
    <a
      key={`${keyPrefix}-l${k}`}
      href={EXTERNAL_LINKS[v]}
      target="_blank"
      rel="noopener noreferrer"
      className="legal-link"
    >
      {v}
    </a>
  ));
  return nodes;
}

/** Un paragraphe : les retours à la ligne simples deviennent des <br>. */
function renderParagraph(block, key) {
  const lines = block.split('\n');
  return (
    <p key={key} className="legal-p">
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 && <br />}
          {renderInline(line, `${key}-${i}`)}
        </React.Fragment>
      ))}
    </p>
  );
}

const splitRow = (line) => line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => c.trim());

/**
 * Tableau. Enveloppé dans un conteneur qui défile horizontalement : sur
 * 390 px, un tableau à trois colonnes de texte ne rentre pas, et c'est le
 * tableau qui doit défiler — jamais la page (cf. conventions 0e71604).
 */
function renderTable(block, key) {
  const lines = block.split('\n').filter((l) => l.trim());
  const header = splitRow(lines[0]);
  const body = lines.slice(2).map(splitRow);
  return (
    <div key={key} className="legal-table-wrap">
      <table className="legal-table">
        <thead>
          <tr>{header.map((c, i) => <th key={i} scope="col">{renderInline(c, `${key}-h${i}`)}</th>)}</tr>
        </thead>
        <tbody>
          {body.map((row, r) => (
            <tr key={r}>{row.map((c, i) => <td key={i}>{renderInline(c, `${key}-${r}-${i}`)}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderList(block, key) {
  const items = block.split('\n').filter((l) => l.trim().startsWith('- '));
  return (
    <ul key={key} className="legal-ul">
      {items.map((it, i) => <li key={i}>{renderInline(it.replace(/^\s*-\s+/, ''), `${key}-${i}`)}</li>)}
    </ul>
  );
}

const isTable = (b) => b.trim().startsWith('|');
const isList = (b) => b.split('\n').every((l) => !l.trim() || l.trim().startsWith('- '));
const HEADING_RE = /^(#{1,6})\s+(.*)$/;

/**
 * Découpe un document en blocs et renvoie { nodes, toc }.
 * `toc` liste les titres de niveau 2 (les sections numérotées), avec leur
 * ancre — c'est ce qui alimente la table des matières des CGV.
 */
export function renderLegalDocument(markdown) {
  const blocks = String(markdown).trim().split(/\n{2,}/);
  const toc = [];
  const nodes = [];

  blocks.forEach((raw, bi) => {
    const block = raw.replace(/\s+$/, '');
    if (!block.trim()) return;
    const key = `b${bi}`;

    const heading = block.match(HEADING_RE);
    if (heading && !block.includes('\n')) {
      const level = heading[1].length;
      const text = heading[2].trim();
      const id = slugify(text);
      if (level === 2) toc.push({ id, text });
      const Tag = `h${Math.min(level, 6)}`;
      nodes.push(
        <Tag key={key} id={id} className={`legal-h${level}`}>
          {renderInline(text, key)}
        </Tag>,
      );
      return;
    }

    if (/^-{3,}$/.test(block.trim())) { nodes.push(<hr key={key} className="legal-hr" />); return; }
    if (isTable(block)) { nodes.push(renderTable(block, key)); return; }
    if (isList(block)) { nodes.push(renderList(block, key)); return; }
    nodes.push(renderParagraph(block, key));
  });

  return { nodes, toc };
}

export default renderLegalDocument;
